import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { RefereeRegistration } from "@/types";
import { refereeContactTemplate } from "@/lib/emailTemplates";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

interface ContactRefereeModalProps {
  registration: RefereeRegistration;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Mismo modal que ContactProviderModal, para las solicitudes de árbitros.
export function ContactRefereeModal({ registration, open, onOpenChange }: ContactRefereeModalProps) {
  const queryClient = useQueryClient();
  const template = refereeContactTemplate(registration);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.html);
  const [confirmingSend, setConfirmingSend] = useState(false);

  useEffect(() => {
    setSubject(template.subject);
    setBody(template.html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration.id]);

  const markContacted = useMutation({
    mutationFn: () => api.put(`/referee-registrations/${registration.id}`, { status: "CONTACTED" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referee-registrations"] });
      toast.success("Marcado como contactado");
    },
    onError: () => toast.error("No se pudo actualizar el registro"),
  });

  const sendEmail = useMutation({
    mutationFn: () => api.post(`/referee-registrations/${registration.id}/contact-email`, { subject, html: body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referee-registrations"] });
      toast.success("Correo enviado");
      setConfirmingSend(false);
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error ?? "No se pudo enviar el correo. Revisa la configuración de MAIL_USER/MAIL_PASS.");
      setConfirmingSend(false);
    },
  });

  function copyPhone() {
    navigator.clipboard.writeText(registration.telefono);
    toast.success("Teléfono copiado");
  }

  const fullName = `${registration.nombre}${registration.apellidos ? ` ${registration.apellidos}` : ""}`;

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Contactar con ${fullName}`} description="Solicitud de registro como árbitro">
      <div className="flex flex-col gap-6">
        <div className="rounded-xl border border-ink-100 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-700">
            <Phone className="h-4 w-4" />
            Por teléfono
          </div>
          <div className="flex items-center gap-2">
            <a href={`tel:${registration.telefono}`} className="flex-1 rounded-lg bg-ink-50 px-3 py-2 text-sm font-medium text-ink-800">
              {registration.telefono}
            </a>
            <Button type="button" onClick={copyPhone} title="Copiar número">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button
            type="button"
            className="mt-3 w-full"
            disabled={markContacted.isPending || registration.status === "CONTACTED"}
            onClick={() => markContacted.mutate()}
          >
            {registration.status === "CONTACTED" ? "Ya contactado" : "Marcar como llamado"}
          </Button>
        </div>

        <div className="rounded-xl border border-ink-100 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-700">
            <Mail className="h-4 w-4" />
            Por correo
          </div>
          <div className="flex flex-col gap-3">
            <Input id="contact-referee-to" label="Para" value={registration.email} disabled />
            <Input id="contact-referee-subject" label="Asunto" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-referee-body" className="text-sm font-medium text-ink-600">
                Mensaje
              </label>
              <textarea
                id="contact-referee-body"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
            <Button type="button" disabled={sendEmail.isPending} onClick={() => setConfirmingSend(true)} className="w-full">
              <Send className="h-4 w-4" />
              {sendEmail.isPending ? "Enviando…" : "Enviar correo"}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingSend}
        onOpenChange={setConfirmingSend}
        title="Enviar correo"
        description={`Se enviará este correo a ${registration.email} ahora mismo. ¿Seguro que quieres enviarlo?`}
        tone="primary"
        confirmLabel="Enviar correo"
        onConfirm={() => sendEmail.mutate()}
      />
    </Modal>
  );
}
